import { Component, ChangeDetectionStrategy, input, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgkitInlineMessageButton } from '../interfaces/ngkit-inline-message-button';
import { NgkitInlineMessageLink } from '../interfaces/ngkit-inline-message-link';

import { Tooltip } from 'primeng/tooltip';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';

/**
 * Union type representing a message action (button or link).
 * Actions can be either clickable buttons with callbacks or navigation links.
 * @see NgkitInlineMessageButton
 * @see NgkitInlineMessageLink
 */
export type NgkitInlineMessageAction = NgkitInlineMessageLink | NgkitInlineMessageButton;

/**
 * Severity level for message styling and semantics.
 * Determines the visual style and icon used for the message.
 */
export type MessageSeverity = 'success' | 'info' | 'warn' | 'error';

/**
 * Inline message component displaying contextual messages with optional actions.
 *
 * Provides a flexible component for displaying messages with:
 * - Multiple severity levels for different message types
 * - Support for custom action buttons and navigation links
 * - Reactive styling based on severity and banner mode
 * - Automatic tooltip display for truncated text
 * - Signal-based state management for optimal performance
 *
 * The component uses Angular 20 standalone architecture and Change Detection Strategy OnPush
 * for optimal performance with signals-based reactivity.
 * @example
 * ```html
 * <ngkit-inline-message
 *   [severity]="'info'"
 *   [title]="'Information'"
 *   [message]="'This is an informational message'"
 *   [actions]="actions"
 *   [bannerMode]="true"
 * />
 * ```
 */
@Component({
  selector: 'ngkit-inline-message',
  templateUrl: './ngkit-inline-message.component.html',
  styleUrl: './ngkit-inline-message.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, Tooltip, Bind, Button],
})
export class NgKitInlineMessageComponent {
  /**
   * Message severity level that controls styling and icon display.
   *
   * Determines the visual appearance and semantic meaning of the message.
   * Maps to CSS classes for consistent styling across the application.
   * Defaults to 'info' for general informational messages.
   * @default 'info'
   * @example
   * ```html
   * <ngkit-inline-message [severity]="'error'" />
   * ```
   * @see MessageSeverity
   */
  readonly severity = input<MessageSeverity>('info');

  /**
   * Optional title text displayed above the message content.
   *
   * When provided, displays as a bold heading. Set to null to hide the title section.
   * Useful for providing context or categorizing messages.
   * @default null
   * @example
   * ```html
   * <ngkit-inline-message [title]="'Warning'" />
   * ```
   */
  readonly title = input<string | null>(null);

  /**
   * Primary message content text to display.
   *
   * The main body of the message. Can be truncated with automatic tooltip display
   * if the text exceeds the available width.
   * @default null
   * @example
   * ```html
   * <ngkit-inline-message [message]="'Operation completed successfully'" />
   * ```
   */
  readonly message = input<string | null>(null);

  /**
   * Array of custom actions (buttons or links) to display in the message.
   *
   * Each action can be either a button with a callback function or a navigation link.
   * Actions are displayed inline at the end of the message content.
   * @default []
   * @example
   * ```typescript
   * const actions = [
   *   {
   *     type: 'button',
   *     title: 'Undo',
   *     callback: () => this.undo()
   *   },
   *   {
   *     type: 'link',
   *     title: 'Learn more',
   *     href: '/documentation'
   *   }
   * ];
   * ```
   * @see NgkitInlineMessageAction
   * @see NgkitInlineMessageButton
   * @see NgkitInlineMessageLink
   */
  readonly actions = input<NgkitInlineMessageAction[]>([]);

  /**
   * Enables full-width banner mode layout for prominent message display.
   *
   * When enabled, centers the message content horizontally and applies banner-specific styling
   * for displaying system-wide notifications or important alerts.
   * @default false
   * @example
   * ```html
   * <ngkit-inline-message [bannerMode]="true" />
   * ```
   */
  readonly bannerMode = input(false);

  /**
   * Aligns action buttons to the right side of the component.
   *
   * When enabled, pushes buttons and links to the right edge using flexbox spacing.
   * Useful for maintaining consistent layout when actions need to appear at different positions.
   * @default false
   * @example
   * ```html
   * <ngkit-inline-message [alignButtonRight]="true" />
   * ```
   */
  readonly alignButtonRight = input(false);

  /**
   * Signal holding the tooltip text to display on message overflow.
   *
   * Automatically set when message text is truncated. Shows full message text as tooltip
   * for improved UX when content exceeds available width.
   * @default ''
   */
  protected readonly tooltipMessage = signal('');

  /**
   * Signal holding computed CSS classes for styling the component.
   *
   * Automatically updated based on severity level and banner mode changes.
   * Contains space-separated class names for BEM naming convention compliance.
   * @default ''
   */
  protected readonly variantClass = signal('');

  /**
   * PrimeNG Button component styling configuration.
   *
   * Defines styling for action buttons displayed in the message component.
   * Uses PrimeNG's unstyled mode with custom DataTable props for consistent appearance.
   * paddingX - Horizontal padding for buttons
   * paddingY - Vertical padding for buttons
   * sm - Small breakpoint styles
   * lg - Large breakpoint styles
   * secondary - Secondary button styling (blue color override)
   */
  inlineMessageButtonDt = {
    paddingX: '10px',
    paddingY: '0px',
    sm: {
      fontSize: '14px',
    },
    lg: {
      fontSize: '14px',
    },
    label: {},
    secondary: {
      color: '#0a57c5 !important',
    },
  };

  /**
   * Reactive effect to update variant CSS classes based on severity and banner mode.
   *
   * Automatically recomputes and applies CSS classes whenever the severity level or banner mode
   * changes. This signal-based approach provides optimal change detection performance without
   * requiring manual change detection calls or template function calls.
   *
   * The effect:
   * 1. Reads current severity level
   * 2. Maps severity to appropriate BEM CSS class
   * 3. Adds banner-specific layout class if banner mode is enabled
   * 4. Updates the variantClass signal with computed classes
   * @example
   * When severity changes from 'info' to 'error':
   * - Previous class: 'ngkit-inline-message--info'
   * - New class: 'ngkit-inline-message--error'
   *
   * When bannerMode is enabled:
   * - Final class: 'ngkit-inline-message--info ngkit-flex-justify-content-center'
   */
  protected readonly updateVariantClass = effect(() => {
    const severityValue = this.severity();
    let variant = '';

    // Map severity levels to CSS classes for consistent styling
    // Default to 'info' if severity is undefined or not recognized
    switch (severityValue) {
      case 'warn':
        variant = 'ngkit-inline-message--warn';
        break;
      case 'error':
        variant = 'ngkit-inline-message--error';
        break;
      case 'success':
        variant = 'ngkit-inline-message--success';
        break;
      default: // info
        variant = 'ngkit-inline-message--info';
        break;
    }

    // Add banner-specific layout class if banner mode is enabled
    // This centers content horizontally for banner-style message display
    if (this.bannerMode()) {
      variant += ' ngkit-flex-justify-content-center';
    }

    this.variantClass.set(variant);
  });

  /**
   * Updates tooltip message when message text is truncated.
   *
   * Displays tooltip with full message text only when the message content exceeds
   * the available width, preventing unnecessary tooltips for messages that fit completely.
   * This improves UX by providing context for truncated text without cluttering the interface.
   *
   * The function uses DOM measurement (offsetWidth vs scrollWidth) to accurately detect
   * text overflow and only sets tooltip when needed.
   * @param el - The HTML element containing the message text to check for overflow
   * @example
   * ```html
   * <div (ngkitResizeObserver)="setMessageTooltip($event)">
   *   {{ message() }}
   * </div>
   * ```
   * Note on tooltip behavior:
   * - Comparison: `offsetWidth` (visible width) vs `scrollWidth` (full content width)
   * - Only sets tooltip if message exists and text is actually overflowing
   * - Tooltip is cleared if text fits within available width
   * @see Tooltip - PrimeNG tooltip directive
   */
  public setMessageTooltip(el: HTMLElement): void {
    // Only show tooltip if the element's content exceeds its visible width
    // This provides context for truncated text without cluttering the UI
    const message = this.message();
    this.tooltipMessage.set(el.offsetWidth < el.scrollWidth && message ? message : '');
  }
}
